import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { isAllowedCtaPath } from "@/lib/announcements";
import { format } from "date-fns";

interface Props {
  workspaceId: string;
}

type Pending = { id: string; title: string; body: string | null; image_url: string | null; cta_label: string | null; cta_path: string | null; published_at?: string | null };

export function AnnouncementView({ a, onClose, onCta }: { a: Pending; onClose?: () => void; onCta?: () => void }) {
  const showCta = a.cta_label && isAllowedCtaPath(a.cta_path);
  const date = a.published_at ? format(new Date(a.published_at), "d MMM yyyy") : null;
  return (
    <div className="flex flex-col p-3">
      {a.image_url ? (
        <div className="aspect-video w-full overflow-hidden rounded-xl bg-muted">
          <img src={a.image_url} alt="" className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="relative aspect-[16/7] w-full overflow-hidden rounded-xl bg-gradient-to-br from-primary/15 via-primary/5 to-accent/30 flex items-center justify-center">
          <div className="absolute -top-10 -left-10 h-40 w-40 rounded-full bg-primary/10 blur-2xl" />
          <div className="absolute -bottom-12 -right-6 h-40 w-40 rounded-full bg-primary/15 blur-2xl" />
          <div className="relative h-14 w-14 rounded-2xl bg-background shadow-sm flex items-center justify-center">
            <Sparkles className="h-7 w-7 text-primary" />
          </div>
        </div>
      )}
      <div className="px-4 pt-5 pb-3 space-y-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary font-semibold px-2.5 py-0.5">
            <Sparkles className="h-3 w-3" /> New
          </span>
          {date && <span className="text-muted-foreground">{date}</span>}
        </div>
        <DialogTitle className="text-2xl font-semibold tracking-tight leading-tight">{a.title || "Announcement title"}</DialogTitle>
        {a.body ? (
          <DialogDescription className="whitespace-pre-line text-[15px] leading-relaxed text-muted-foreground">{a.body}</DialogDescription>
        ) : (
          <DialogDescription className="sr-only">Announcement</DialogDescription>
        )}
        <div className="pt-3 space-y-1.5">
          {showCta ? (
            <>
              <Button className="w-full h-11 text-sm font-medium" onClick={onCta}>{a.cta_label}</Button>
              <Button variant="ghost" className="w-full h-9 text-sm text-muted-foreground" onClick={onClose}>Maybe later</Button>
            </>
          ) : (
            <Button className="w-full h-11 text-sm font-medium" onClick={onClose}>Got it</Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AnnouncementDialog({ workspaceId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") qc.removeQueries({ queryKey: ["announcement"] });
      setUserId(session?.user?.id ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  const key = ["announcement", workspaceId, userId];
  const { data } = useQuery({
    queryKey: key,
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("get_pending_announcement", { _workspace_id: workspaceId });
      if (error) return null;
      return ((data as Pending[]) ?? [])[0] ?? null;
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [data?.id]);

  if (!data) return null;

  const mark = (action: "dismissed" | "cta_clicked") => {
    setOpen(false);
    (supabase.rpc as any)("mark_announcements_seen", { _workspace_id: workspaceId, _shown_id: data.id, _action: action })
      .then(() => qc.setQueryData(key, null));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) mark("dismissed"); }}>
      <DialogContent className="p-0 overflow-hidden max-w-[520px] gap-0 rounded-2xl">
        <AnnouncementView
          a={data}
          onClose={() => mark("dismissed")}
          onCta={() => { mark("cta_clicked"); if (data.cta_path) navigate(data.cta_path); }}
        />
      </DialogContent>
    </Dialog>
  );
}
